/**
 * EmergencyResponsePage - Emergency Response Plan Management
 * TurboTax-style guided wizard for BCM Manager
 * 
 * Flow: Event Identification → Initial Communication → Damage Assessment
 * → Incident Declaration → Incident Communication → BCP/DRP Activation
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { persistAcceptedSuggestions, suggestionName } from "../utils/acceptSuggestions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import StepDialog from "../components/StepDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  AlertTriangle,
  Bell,
  Shield,
  Activity,
  Radio,
  Play,
  CheckCircle2,
  ArrowRight,
  RefreshCw,
  Siren,
  FileText,
  Users,
  Building2,
  Server,
  Globe,
  Zap,
  Clock,
  Target,
  MessageSquare,
  Phone,
  Eye,
  Send,
} from "lucide-react";

const SEVERITY_COLORS = {
  critical: "bg-red-100 text-red-700 border-red-300",
  high: "bg-sky-100 text-sky-700 border-sky-300",
  medium: "bg-yellow-100 text-yellow-700 border-yellow-300",
  low: "bg-green-100 text-green-700 border-green-300",
};

const STEP_ICONS = {
  1: AlertTriangle,
  2: Globe,
  3: Activity,
  4: MessageSquare,
  5: Bell,
  6: Phone,
  7: Server,
  8: Building2,
  9: Users,
  10: Siren,
  11: Shield,
  12: Play,
};

export default function EmergencyResponsePage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wizardQuestions, setWizardQuestions] = useState(null);
  const [orgContext, setOrgContext] = useState(null);
  const [showWizardDialog, setShowWizardDialog] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardAnswers, setWizardAnswers] = useState({});
  const [recommendation, setRecommendation] = useState(null);
  const [erpPlans, setErpPlans] = useState([]);
  const [activeIncidents, setActiveIncidents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [erpName, setErpName] = useState("");
  const [selectedErp, setSelectedErp] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Redirect business users - BCM Manager only page
  useEffect(() => {
    if (user && (user.role === "process_manager" || user.role === "process_manager")) {
      toast.error("Emergency Response is only available to BCM Managers");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [questionsRes, plansRes, incidentsRes] = await Promise.all([
        api.get("/emergency-response/wizard/questions"),
        api.get("/emergency-response/plans"),
        api.get("/emergency-response/incidents/active"),
      ]);
      
      setWizardQuestions(questionsRes.data.questions);
      setOrgContext(questionsRes.data.org_context);
      setErpPlans(plansRes.data.plans || []);
      setActiveIncidents(incidentsRes.data.incidents || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load emergency response data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Accept AI emergency-response suggestions → create draft ERPs via the SAME
  // manual endpoint (POST /emergency-response/create). Dedupe by name; follows
  // the persistAcceptedSuggestions pattern used across the app.
  const handleAcceptEmergencySuggestions = async (items) => {
    const existingNames = (erpPlans || []).map((p) => p.name);
    const result = await persistAcceptedSuggestions({
      items,
      existingKeys: existingNames,
      keyOf: (raw) => suggestionName(raw),
      createOne: async (raw) => {
        const o = raw && typeof raw === "object" ? raw : {};
        const name = suggestionName(raw);
        const description = String(o.description || o.erp_template || "");
        // Remaining AI content is stored in ai_recommendations (the designated AI-text store).
        const aiRec = { source_suggestion: o, accepted_at: new Date().toISOString() };
        await api.post("/emergency-response/create", {
          name,
          description,
          ai_recommendations: aiRec,
          source: "ai_suggestion",
        });
      },
    });
    await fetchData();
    return result;
  };

  const startWizard = () => {
    setWizardStep(0);
    setWizardAnswers({});
    setRecommendation(null);
    setErpName(`ERP - ${new Date().toLocaleDateString()}`);
    setShowWizardDialog(true);
  };

  const handleSingleAnswer = (questionId, value) => {
    setWizardAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMultiAnswer = (questionId, value, checked) => {
    setWizardAnswers(prev => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, value] };
      } else {
        return { ...prev, [questionId]: current.filter(v => v !== value) };
      }
    });
  };

  const getAnalysis = async () => {
    try {
      const response = await api.post("/emergency-response/wizard/analyze", {
        answers: wizardAnswers
      });
      setRecommendation(response.data);
    } catch (error) {
      console.error("Error getting analysis:", error);
      toast.error("Failed to analyze responses");
    }
  };

  const handleNext = async () => {
    if (wizardStep === 8) {
      // After damage assessment, get AI recommendation
      await getAnalysis();
    }
    setWizardStep(wizardStep + 1);
  };

  const handleCreateERP = async () => {
    if (!erpName.trim()) {
      toast.error("Please enter a plan name");
      return;
    }
    
    setSubmitting(true);
    try {
      const erpData = {
        name: erpName,
        description: `Emergency Response Plan created on ${new Date().toLocaleDateString()}`,
        scope: wizardAnswers.impact_radius || "site_wide",
        event_identification: {
          event_type: wizardAnswers.event_type || "other",
          event_description: wizardAnswers.event_description || "",
          event_location: wizardAnswers.event_location || "",
          detected_by: user?.name || "BCM Manager",
          detection_time: new Date().toISOString(),
          initial_severity: wizardAnswers.initial_severity || "medium",
          affected_areas: wizardAnswers.affected_areas || [],
          estimated_impact_radius: wizardAnswers.impact_radius || "single_building"
        },
        initial_communication: {
          who_to_notify: wizardAnswers.who_to_notify || [],
          communication_method: wizardAnswers.communication_method || []
        },
        damage_assessment: {
          it_systems_affected: wizardAnswers.it_impact || [],
          facility_impact: wizardAnswers.facility_impact || "fully_operational",
          personnel_status: wizardAnswers.personnel_status || "all_accounted"
        },
        incident_declaration: {
          incident_type: wizardAnswers.declare_incident || "no_monitor",
          declaration_authority: user?.name || "BCM Manager",
          declaration_time: new Date().toISOString(),
          incident_commander: wizardAnswers.incident_commander || "",
          command_center_location: "Main Office",
          initial_priorities: ["Life Safety", "Business Continuity"]
        },
        plan_activation: {
          activation_type: wizardAnswers.activation_decision || recommendation?.recommendation?.activation_type || "none",
          activation_authority: user?.name || "BCM Manager",
          activation_time: new Date().toISOString(),
          estimated_recovery_hours: recommendation?.recommendation?.estimated_recovery_hours || 24
        },
        ai_recommendations: recommendation
      };
      
      const response = await api.post("/emergency-response/create", erpData);
      toast.success("Emergency Response Plan created successfully");
      setShowWizardDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error creating ERP:", error);
      toast.error("Failed to create Emergency Response Plan");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = (plan) => {
    setSelectedErp(plan);
    setShowDetailDialog(true);
  };

  const handleActivateIncident = async (plan) => {
    setSubmitting(true);
    try {
      await api.post("/emergency-response/incidents/activate", {
        erp_id: plan.id,
        activation_type: plan.plan_activation?.activation_type || "both",
        incident_commander: user?.name || "BCM Manager",
        initial_priorities: ["Life Safety", "Business Continuity"],
      });
      toast.success("Incident activated! BCP/DRP plans will be triggered.");
      setShowDetailDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error activating incident:", error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Failed to activate incident";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolveIncident = async (incidentId) => {
    setSubmitting(true);
    try {
      await api.post(`/emergency-response/incidents/${incidentId}/resolve`, {
        resolution_notes: "Incident resolved",
      });
      toast.success("Incident resolved successfully");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Failed to resolve incident";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendCrisisAlert = async (plan) => {
    if (!plan) return;
    
    setSubmitting(true);
    try {
      await api.post("/emergency-response/crisis-alert", {
        erp_id: plan.id,
        erp_name: plan.name,
        event_type: plan.event_identification?.event_type || "other",
        severity: plan.event_identification?.initial_severity || "medium",
        affected_areas: plan.event_identification?.affected_areas || [],
        who_to_notify: plan.initial_communication?.who_to_notify || [],
        communication_methods: plan.initial_communication?.communication_method || [],
        message: `CRISIS ALERT: ${plan.name}\n\nEvent Type: ${plan.event_identification?.event_type?.replace(/_/g, " ")}\nSeverity: ${plan.event_identification?.initial_severity?.toUpperCase()}\nAffected Areas: ${plan.event_identification?.affected_areas?.join(", ") || "Under assessment"}\n\nImmediate action required. Please standby for further instructions.`
      });
      toast.success("Crisis alert sent to all designated stakeholders!");
    } catch (error) {
      console.error("Error sending crisis alert:", error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Failed to send crisis alert";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const getCurrentQuestions = () => {
    if (!wizardQuestions) return [];
    
    const sections = Object.keys(wizardQuestions);
    let stepCount = 0;
    
    for (const section of sections) {
      for (let i = 0; i < wizardQuestions[section].length; i++) {
        if (stepCount === wizardStep) {
          return [wizardQuestions[section][i]];
        }
        stepCount++;
      }
    }
    return [];
  };

  const getTotalSteps = () => {
    if (!wizardQuestions) return 12;
    return Object.values(wizardQuestions).reduce((sum, arr) => sum + arr.length, 0);
  };

  const getWizardStepDefs = () => {
    if (!wizardQuestions) return [];
    const steps = [];
    const sections = Object.keys(wizardQuestions);
    for (const section of sections) {
      for (let i = 0; i < wizardQuestions[section].length; i++) {
        const q = wizardQuestions[section][i];
        steps.push({
          id: q.id || `step-${steps.length}`,
          title: q.title || `Step ${steps.length + 1}`,
          subtitle: q.question || "",
          isComplete: wizardAnswers[q.id] !== undefined,
        });
      }
    }
    // Add final "Save" step
    steps.push({ id: "save_erp", title: "Save Plan", subtitle: "Name and save your Emergency Response Plan", isComplete: false });
    return steps;
  };

  const StepIcon = STEP_ICONS[wizardStep + 1] || AlertTriangle;

  if (loading && !wizardQuestions) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="emergency-response-page">
      <AISuggestionsBanner module="emergency_response" onAccept={handleAcceptEmergencySuggestions}
        existingKeys={(erpPlans || []).map((p) => p.name)}
        itemKeyOf={(raw) => suggestionName(raw)} />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-red-100 text-red-700">EMERGENCY</Badge>
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">ISO 22301:8.4.4</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Emergency Response</h1>
          <p className="text-slate-600 mt-1">
            AI-powered incident management and plan activation
          </p>
        </div>
        <Button onClick={startWizard} className="bg-red-600 hover:bg-red-700" data-testid="start-erp-wizard">
          <Siren className="w-4 h-4 mr-2" />
          Start Emergency Response Wizard
        </Button>
      </div>

      {/* Active Incidents Alert */}
      {activeIncidents.length > 0 && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Siren className="w-8 h-8 text-red-600 animate-pulse" />
              <div>
                <h3 className="font-semibold text-red-800">{activeIncidents.length} Active Incident(s)</h3>
                <p className="text-sm text-red-700">
                  {activeIncidents.map(i => i.erp_name).join(", ")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organization Context */}
      {orgContext && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Business Processes</p>
                  <p className="text-2xl font-bold text-slate-900">{orgContext.total_processes}</p>
                </div>
                <FileText className="w-8 h-8 text-sky-700 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Critical (0-4h RTO)</p>
                  <p className="text-2xl font-bold text-red-600">{orgContext.rto_distribution?.critical_0_4h || 0}</p>
                </div>
                <Zap className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Urgent (4-24h RTO)</p>
                  <p className="text-2xl font-bold text-sky-700">{orgContext.rto_distribution?.urgent_4_24h || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-sky-700 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Business Units</p>
                  <p className="text-2xl font-bold text-slate-900">{orgContext.business_units_count}</p>
                </div>
                <Building2 className="w-8 h-8 text-slate-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ERP Plans List */}
      <Card data-testid="erp-plans-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-slate-400" />
            Emergency Response Plans
          </CardTitle>
          <CardDescription>
            Prepared plans for various emergency scenarios
          </CardDescription>
        </CardHeader>
        <CardContent>
          {erpPlans.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-2">No Emergency Response Plans created yet</p>
              <p className="text-sm text-slate-400">
                Use the wizard above to create your first ERP
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {erpPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                  data-testid={`erp-${plan.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{plan.name}</h3>
                        <Badge className={plan.status === "activated" ? "bg-red-100 text-red-700" : "bg-slate-100"}>
                          {plan.status?.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">{plan.description}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Created: {new Date(plan.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleViewDetails(plan)} data-testid={`view-erp-${plan.id}`}>
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* TurboTax Wizard Dialog */}
      <StepDialog
        open={showWizardDialog}
        onOpenChange={setShowWizardDialog}
        title="Emergency Response Wizard"
        steps={getWizardStepDefs()}
        currentStep={wizardStep}
        onStepChange={setWizardStep}
        onSubmit={handleCreateERP}
        onCancel={() => setShowWizardDialog(false)}
        submitLabel={submitting ? "Creating..." : "Create Emergency Response Plan"}
        submitDisabled={submitting || !erpName.trim()}
        saving={submitting}
        canAdvance={true}
        maxWidth="max-w-3xl"
      >
        {/* Question Steps */}
        {wizardStep < getTotalSteps() && (
          <div className="py-4">
            {getCurrentQuestions().map((question) => (
              <div key={question.id} className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <StepIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{question.title}</h3>
                    <p className="text-slate-600">{question.question}</p>
                  </div>
                </div>

                {question.type === "single_choice" && (
                  <div className="space-y-2 ml-12">
                    {question.options.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => handleSingleAnswer(question.id, option.id)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          wizardAnswers[question.id] === option.id
                            ? "border-red-500 bg-red-50"
                            : "border-slate-200 hover:border-red-300"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{option.label}</p>
                            {option.examples && (
                              <p className="text-xs text-slate-500">{option.examples}</p>
                            )}
                            {option.triggers && (
                              <p className="text-xs text-red-600 mt-1">&rarr; {option.triggers}</p>
                            )}
                          </div>
                          {wizardAnswers[question.id] === option.id && (
                            <CheckCircle2 className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {question.type === "multi_choice" && (
                  <div className="space-y-2 ml-12">
                    {question.options.map((option) => (
                      <div
                        key={option.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          (wizardAnswers[question.id] || []).includes(option.id)
                            ? "border-red-500 bg-red-50"
                            : "border-slate-200 hover:border-red-300"
                        }`}
                        onClick={() => handleMultiAnswer(
                          question.id, 
                          option.id, 
                          !(wizardAnswers[question.id] || []).includes(option.id)
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={(wizardAnswers[question.id] || []).includes(option.id)}
                            onCheckedChange={(checked) => handleMultiAnswer(question.id, option.id, checked)}
                          />
                          <span>{option.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* AI Recommendation Section (appears after damage assessment) */}
            {wizardStep > 8 && recommendation && (
              <div className="mt-6 p-4 bg-sky-50 rounded-lg border border-sky-200">
                <div className="flex items-start gap-3">
                  <Target className="w-6 h-6 text-sky-700 mt-1" />
                  <div>
                    <h4 className="font-semibold text-indigo-800">AI Recommendation</h4>
                    <p className="text-sm text-sky-700 mt-1">
                      <strong>Recommended Activation:</strong>{" "}
                      {recommendation.recommendation?.activation_type === "both" 
                        ? "Both BCP and IT-DRP" 
                        : recommendation.recommendation?.activation_type === "bcp_only"
                        ? "BCP Only (Facility, People, Operations)"
                        : recommendation.recommendation?.activation_type === "drp_only"
                        ? "IT-DRP Only (IT Systems)"
                        : "Continue Monitoring"}
                    </p>
                    <p className="text-sm text-sky-700 mt-1">
                      Confidence: {recommendation.recommendation?.confidence?.toUpperCase()}
                    </p>
                    {recommendation.recommendation?.reasoning?.map((reason, i) => (
                      <p key={i} className="text-xs text-sky-700 mt-1">&bull; {reason}</p>
                    ))}
                    {recommendation.bcp_plans?.length > 0 && (
                      <p className="text-sm text-sky-700 mt-2">
                        {recommendation.bcp_plans.length} BCP plan(s) available for activation
                      </p>
                    )}
                    {recommendation.drp_plans?.length > 0 && (
                      <p className="text-sm text-sky-700">
                        {recommendation.drp_plans.length} IT-DRP plan(s) available for activation
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Final Step - Save ERP */}
        {wizardStep === getTotalSteps() && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label>Emergency Response Plan Name</Label>
              <Input
                value={erpName}
                onChange={(e) => setErpName(e.target.value)}
                placeholder="Enter plan name..."
                className="mt-2"
              />
            </div>
          </div>
        )}
      </StepDialog>

      {/* ERP Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              {selectedErp?.name}
            </DialogTitle>
            <DialogDescription>
              Emergency Response Plan Details
            </DialogDescription>
          </DialogHeader>

          {selectedErp && (
            <div className="space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${
                selectedErp.status === "activated" 
                  ? "bg-red-100 border border-red-300" 
                  : "bg-slate-100"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={selectedErp.status === "activated" ? "bg-red-600" : "bg-slate-600"}>
                      {selectedErp.status?.toUpperCase()}
                    </Badge>
                    <span className="text-sm text-slate-600">
                      Created: {new Date(selectedErp.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {selectedErp.status === "draft" && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => handleSendCrisisAlert(selectedErp)}
                        disabled={submitting}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="send-crisis-alert-btn"
                      >
                        {submitting ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Crisis Alert
                      </Button>
                      <Button 
                        onClick={() => handleActivateIncident(selectedErp)}
                        disabled={submitting}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="activate-incident-btn"
                      >
                        {submitting ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Activate Incident
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Event Identification */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Event Identification
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Event Type</p>
                      <p className="font-medium capitalize">
                        {selectedErp.event_identification?.event_type?.replace(/_/g, " ") || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Initial Severity</p>
                      <Badge className={SEVERITY_COLORS[selectedErp.event_identification?.initial_severity] || "bg-slate-100"}>
                        {selectedErp.event_identification?.initial_severity?.toUpperCase() || "N/A"}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Impact Radius</p>
                      <p className="font-medium capitalize">
                        {selectedErp.event_identification?.estimated_impact_radius?.replace(/_/g, " ") || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Detected By</p>
                      <p className="font-medium">{selectedErp.event_identification?.detected_by || "N/A"}</p>
                    </div>
                    {selectedErp.event_identification?.affected_areas?.length > 0 && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-slate-500 mb-2">Affected Areas</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedErp.event_identification.affected_areas.map((area, i) => (
                            <Badge key={i} variant="outline">{area}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Damage Assessment */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    Damage Assessment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Facility Impact</p>
                      <p className="font-medium capitalize">
                        {selectedErp.damage_assessment?.facility_impact?.replace(/_/g, " ") || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Personnel Status</p>
                      <p className="font-medium capitalize">
                        {selectedErp.damage_assessment?.personnel_status?.replace(/_/g, " ") || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">IT Systems Affected</p>
                      <p className="font-medium">
                        {selectedErp.damage_assessment?.it_systems_affected?.length || 0} systems
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Initial Communication */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Bell className="w-5 h-5 text-sky-700" />
                    Initial Communication
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500 mb-2">Who to Notify</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedErp.initial_communication?.who_to_notify?.map((person, i) => (
                          <Badge key={i} variant="outline" className="capitalize">
                            {person?.replace(/_/g, " ")}
                          </Badge>
                        )) || <span className="text-slate-400">None specified</span>}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-2">Communication Methods</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedErp.initial_communication?.communication_method?.map((method, i) => (
                          <Badge key={i} variant="outline" className="capitalize">
                            {method?.replace(/_/g, " ")}
                          </Badge>
                        )) || <span className="text-slate-400">None specified</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Plan Activation */}
              <Card className="border-red-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Play className="w-5 h-5 text-red-600" />
                    Plan Activation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Activation Type</p>
                      <Badge className={
                        selectedErp.plan_activation?.activation_type === "both" 
                          ? "bg-red-100 text-red-700"
                          : selectedErp.plan_activation?.activation_type === "bcp_only"
                          ? "bg-emerald-100 text-emerald-700"
                          : selectedErp.plan_activation?.activation_type === "drp_only"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-slate-100"
                      }>
                        {selectedErp.plan_activation?.activation_type === "both" 
                          ? "BCP + IT-DRP"
                          : selectedErp.plan_activation?.activation_type === "bcp_only"
                          ? "BCP Only"
                          : selectedErp.plan_activation?.activation_type === "drp_only"
                          ? "IT-DRP Only"
                          : "Monitoring"}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Activation Authority</p>
                      <p className="font-medium">{selectedErp.plan_activation?.activation_authority || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Est. Recovery Time</p>
                      <p className="font-medium">{selectedErp.plan_activation?.estimated_recovery_hours || "N/A"} hours</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* AI Recommendations */}
              {selectedErp.ai_recommendations && (
                <Card className="border-sky-200 bg-sky-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2 text-indigo-800">
                      <Target className="w-5 h-5" />
                      AI Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-sky-700">Confidence:</span>
                        <Badge className="bg-sky-100 text-sky-700">
                          {selectedErp.ai_recommendations.recommendation?.confidence?.toUpperCase()}
                        </Badge>
                      </div>
                      {selectedErp.ai_recommendations.recommendation?.reasoning?.length > 0 && (
                        <div>
                          <p className="text-sm text-sky-700 mb-2">Reasoning:</p>
                          <ul className="space-y-1">
                            {selectedErp.ai_recommendations.recommendation.reasoning.map((reason, i) => (
                              <li key={i} className="text-sm text-sky-700 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                {reason}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {selectedErp.ai_recommendations.bcp_plans?.length > 0 && (
                        <p className="text-sm text-sky-700">
                          {selectedErp.ai_recommendations.bcp_plans.length} BCP plan(s) available
                        </p>
                      )}
                      {selectedErp.ai_recommendations.drp_plans?.length > 0 && (
                        <p className="text-sm text-sky-700">
                          {selectedErp.ai_recommendations.drp_plans.length} IT-DRP plan(s) available
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
            {selectedErp?.status === "draft" && (
              <Button 
                onClick={() => handleActivateIncident(selectedErp)}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700"
              >
                {submitting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Activate Incident
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
